import { createContext, ReactNode, useContext, useState } from "react";
import { ToppingModel } from "../../infrastructure/model/ProductItemModel";
import { ITEM_TYPE_ENUM } from "../../applications/constants/AppConst";

export interface OrderItem {
  indexAdd?: number;
  id: number;
  name: string;
  quantity: number;
  size: number;
  price: number;
  moreTopping?: ToppingModel[];
  note?: string;
  image?: string;
  type: ITEM_TYPE_ENUM;
}

interface OrderListContextType {
  orderList: OrderItem[];
  addToOrder: (item: OrderItem) => void;
  updateOrder: (newItem: OrderItem) => void;
  removeFromOrder: (id: number) => void;
  clearOrder: () => void;
}

const OrderListContext = createContext<OrderListContextType | undefined>(undefined);

export const useOrderList = () => {
  const context = useContext(OrderListContext);
  if (!context) {
    console.error("useOrderList must be used within an OrderListProvider");
    return {} as OrderListContextType;
  }
  return context;
};

export const OrderListProvider = ({ children }: { children: ReactNode }) => {
  const [orderList, setOrderList] = useState<OrderItem[]>([]);

  const addToOrder = (item: OrderItem) => {
    setOrderList((prev) => [...prev, { ...item, indexAdd: (prev?.length || 0) + 1 }]);
  };

  const updateOrder = (newOrder: OrderItem) => {
    const indexItem = orderList?.findIndex((e) => e.indexAdd === newOrder.indexAdd);
    if (indexItem === -1) return;
    setOrderList([
      ...orderList.slice(0, indexItem),
      {
        ...orderList[indexItem],
        quantity: newOrder.quantity,
        note: newOrder.note,
        moreTopping: newOrder.moreTopping,
        size: newOrder.size,
        price: newOrder.price,
        name: newOrder.name,
      },
      ...orderList.slice(indexItem + 1),
    ]);
  };

  const removeFromOrder = (indexAdd: number) => {
    setOrderList(orderList.filter((item) => item.indexAdd !== indexAdd));
  };

  const clearOrder = () => {
    setOrderList([]);
  };

  return (
    <OrderListContext.Provider
      value={{ orderList, addToOrder, updateOrder, removeFromOrder, clearOrder }}
    >
      {children}
    </OrderListContext.Provider>
  );
};
